import { useDeleteCargoScheduledCarrier } from "@/entities/delete-cargo-scheduled-carrier/fetch";
import { useGetOffersCargo } from "@/entities/get-offers-cargo/fetch";
import { useMultipleGetVehicle } from "@/entities/get-vehicle/fetch";
import { useGetVehicles } from "@/entities/get-vehicles/fetch";
import { usePostOffersCargoScheduledCarriers } from "@/entities/post-offers-cargo-scheduled-carriers/fetch";
import { usePostOffersCargosApprove } from "@/entities/post-offers-cargos-approve/fetch";
import { usePostOffersCargosReject } from "@/entities/post-offers-cargos-reject/fetch";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useUpdateCargoScheduledCarrier } from "@/entities/update-cargo-scheduled-carrier/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import {
  isContractValid,
  isDeliveryProposalValid,
} from "@/features/offer-cargo-process-status";
import { fetchOfferCargoProcess } from "@/features/offer-cargo-process/fetch";
import { calculateDeliveryProposalInputValues } from "@/pages/carrier-delivery-proposal-create/input-values";
import { useAuth } from "@/shared/auth/fetch/useAuth";
import { excludeNull } from "@/shared/lib/collection";
import { fromIso8601DateTime } from "@/shared/lib/datetime";
import { isSuccessStatusCode } from "@/shared/lib/http";
import { transformLoadable, transformLoadables } from "@/shared/lib/loadable";
import { replaceNullWithUndefined } from "@/shared/lib/object";
import { loadableFromUseQueryResult } from "@/shared/lib/tanstack-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import type { CarrierCargoDetailPageProps } from "../model";

export const useCarrierCargoDetailPageProps = (
  offerCargoIdStr: string | undefined,
): CarrierCargoDetailPageProps => {
  const auth = useAuth();

  // Local Storage
  // - 新規配送提案作成時に、記入途中の情報をLocalStorageから削除する
  // - 再度配送提案作成時に、既存の提案の内容をdefault値として書き込む
  const [_, setCarrierDeliveryProposalCreatePageCache, removeInProgressValues] =
    useInProgressValuesLocalCache<unknown>("CarrierDeliveryProposalCreatePage");

  // Backend API
  const offerCargoId = offerCargoIdStr
    ? Number.parseInt(offerCargoIdStr)
    : undefined;
  const getDraftOfferCargo = useGetOffersCargo(
    { id: offerCargoId ?? -1 },
    {
      enabled: offerCargoId !== undefined,
    },
  );

  const postOffersCargosApprove = usePostOffersCargosApprove();
  const postOffersCargosReject = usePostOffersCargosReject();

  // 配送予定車両関連のAPI
  const postOffersCargoScheduledCarriers =
    usePostOffersCargoScheduledCarriers();
  const updateCargoScheduledCarrier = useUpdateCargoScheduledCarrier();
  const deleteCargoScheduledCarrier = useDeleteCargoScheduledCarrier();

  // 車両情報取得
  const getVehicles = useGetVehicles(
    {
      search: {
        companyId: auth.session.isAuthenticated
          ? auth.session.user.companyId
          : undefined,
      },
      pagination: {
        displayLimit: 10000,
      },
    },
    {
      enabled: auth.session.isAuthenticated,
    },
  );

  const getMultipleVehicles = useMultipleGetVehicle(
    Object.fromEntries(
      getVehicles.data?.data.vehicles.map((vehicle) => [
        vehicle.id,
        { id: vehicle.id },
      ]) ?? [],
    ),
    {
      enabled: !!getVehicles.data?.data.vehicles,
    },
  );

  // Global Notification
  const { setGlobalNotification } = useGlobalNotification();

  // Props
  const { offerCargoProcess, refetch: refetchOfferCargoProcess } =
    fetchOfferCargoProcess(getDraftOfferCargo.data?.data.process.id);
  const myLatestValidDeliveryProposalOfferCargo = transformLoadable(
    offerCargoProcess,
    (process) => {
      return process.deliveryProposalOfferCargos
        .filter((offerCargo) => {
          if (!auth.session.isAuthenticated) {
            throw new Error("User is not authenticated");
          }
          return (
            isDeliveryProposalValid(process, offerCargo) &&
            offerCargo.company?.id === auth.session.user.companyId
          );
        })
        .sort((a, b) => {
          return (
            fromIso8601DateTime(b.createdAt).getTime() -
            fromIso8601DateTime(a.createdAt).getTime()
          );
        })[0];
    },
  );
  const latestOnGoingContractOfferCargo = transformLoadables(
    [offerCargoProcess, myLatestValidDeliveryProposalOfferCargo],
    (process, deliveryProposal) => {
      if (!deliveryProposal) {
        return undefined;
      }
      return process.contractOfferCargos.find((offerCargo) => {
        if (!auth.session.isAuthenticated) {
          throw new Error("User is not authenticated");
        }
        return (
          isContractValid(process, offerCargo) &&
          offerCargo.sourceOfferCargoId === deliveryProposal.id
        );
      });
    },
  );

  // Actions
  const navigate = useNavigate();
  const onProposeClick: CarrierCargoDetailPageProps["onProposeClick"] =
    useCallback(() => {
      if (!offerCargoIdStr) {
        console.error("Invalid offerCargoIdStr");
        return;
      }
      removeInProgressValues();
      navigate({
        to: "/carrier/cargos/$id/delivery-proposals/create",
        params: { id: offerCargoIdStr },
      });
    }, [navigate, removeInProgressValues, offerCargoIdStr]);
  const onApproveContractClick: CarrierCargoDetailPageProps["onApproveContractClick"] =
    useCallback(() => {
      void (async () => {
        if (
          latestOnGoingContractOfferCargo.status !== "success" ||
          !latestOnGoingContractOfferCargo.data
        ) {
          console.error(
            "[CarrierCargoDetailPageProps] latestOnGoingContractOfferCargo is not loaded",
          );
          return;
        }
        const approveResult = await postOffersCargosApprove.mutateAsync({
          id: latestOnGoingContractOfferCargo.data.id.toString(),
        });
        if (!isSuccessStatusCode(approveResult.status)) {
          setGlobalNotification({
            variant: "error",
            message: "契約の合意に失敗しました。",
          });
          return;
        }
        setGlobalNotification({
          variant: "success",
          message: "契約に合意しました。",
        });
        await refetchOfferCargoProcess();
      })();
    }, [
      latestOnGoingContractOfferCargo,
      postOffersCargosApprove,
      refetchOfferCargoProcess,
      setGlobalNotification,
    ]);

  const onRejectContractClick: CarrierCargoDetailPageProps["onRejectContractClick"] =
    useCallback(
      (params) => {
        void (async () => {
          if (
            latestOnGoingContractOfferCargo.status !== "success" ||
            !latestOnGoingContractOfferCargo.data
          ) {
            console.error(
              "[CarrierCargoDetailPageProps] latestOnGoingContractOfferCargo is not loaded",
            );
            return;
          }
          const rejectResult = await postOffersCargosReject.mutateAsync({
            id: latestOnGoingContractOfferCargo.data.id.toString(),
            data: params,
          });
          if (!isSuccessStatusCode(rejectResult.status)) {
            setGlobalNotification({
              variant: "error",
              message: "契約の見送りに失敗しました。",
            });
            return;
          }
          setGlobalNotification({
            variant: "success",
            message: "契約を見送りました。",
          });
          await Promise.all([
            refetchOfferCargoProcess(),
            getDraftOfferCargo.refetch(),
          ]);
        })();
      },
      [
        latestOnGoingContractOfferCargo,
        postOffersCargosReject,
        refetchOfferCargoProcess,
        getDraftOfferCargo,
        setGlobalNotification,
      ],
    );
  const onProposeAgainClick = useCallback(() => {
    if (offerCargoProcess.status !== "success") {
      return;
    }
    // OfferCargoの構造とUIの構造が異なるため変換している
    const convertedDeliveryProposalOfferCargo =
      calculateDeliveryProposalInputValues(
        offerCargoProcess.data.draftOfferCargo,
      );
    setCarrierDeliveryProposalCreatePageCache(
      replaceNullWithUndefined(convertedDeliveryProposalOfferCargo),
    );
    navigate({
      to: "/carrier/cargos/$id/delivery-proposals/create",
      params: { id: offerCargoProcess.data.draftOfferCargo.id.toString() },
    });
  }, [
    offerCargoProcess.status,
    offerCargoProcess.data,
    navigate,
    setCarrierDeliveryProposalCreatePageCache,
  ]);

  // 車両情報の処理
  const vehicles = useMemo(() => {
    const vehicleLoadables = Object.values(getMultipleVehicles).map(
      (vehicleQuery) => {
        return transformLoadable(
          loadableFromUseQueryResult(vehicleQuery),
          (vehicle) => vehicle.data,
        );
      },
    );
    if (vehicleLoadables.some((vehicle) => vehicle.status === "error")) {
      return {
        status: "error" as const,
        error: {
          message: vehicleLoadables
            .filter((vehicle) => vehicle.status === "error")
            .map((vehicle) => vehicle.error?.message)
            .join(", "),
        },
      };
    }
    if (vehicleLoadables.some((vehicle) => vehicle.status === "loading")) {
      return {
        status: "loading" as const,
        data: undefined,
      };
    }
    return {
      status: "success" as const,
      data: excludeNull(vehicleLoadables.map((vehicle) => vehicle.data)),
    };
  }, [getMultipleVehicles]);

  // 配送予定車両のCRUD操作
  const onCreateScheduledCarrier: CarrierCargoDetailPageProps["onCreateScheduledCarrier"] =
    useCallback(
      async (data) => {
        if (
          latestOnGoingContractOfferCargo.status !== "success" ||
          !latestOnGoingContractOfferCargo.data
        ) {
          console.error("latestOnGoingContractOfferCargo is not loaded");
          return;
        }
        const result = await postOffersCargoScheduledCarriers.mutateAsync({
          data: {
            offerCargoId: latestOnGoingContractOfferCargo.data.id,
            vehicleNumber: data.vehicleNumber || null,
            contactName: data.contactName || null,
            contactNameKana: data.contactNameKana || null,
            name: data.name || null,
            phoneNumber: data.phoneNumber || null,
          },
        });
        if (isSuccessStatusCode(result.status)) {
          await Promise.all([
            refetchOfferCargoProcess(),
            getDraftOfferCargo.refetch(),
          ]);
          setGlobalNotification({
            variant: "success",
            message: "配送予定車両を登録しました",
          });
        } else {
          setGlobalNotification({
            variant: "error",
            message: "配送予定車両の登録に失敗しました",
          });
        }
      },
      [
        latestOnGoingContractOfferCargo,
        postOffersCargoScheduledCarriers,
        refetchOfferCargoProcess,
        getDraftOfferCargo,
        setGlobalNotification,
      ],
    );

  const onUpdateScheduledCarrier: CarrierCargoDetailPageProps["onUpdateScheduledCarrier"] =
    useCallback(
      async (data) => {
        if (
          latestOnGoingContractOfferCargo.status !== "success" ||
          !latestOnGoingContractOfferCargo.data?.scheduledCarrier
        ) {
          console.error("scheduledCarrier is not loaded");
          return;
        }
        const result = await updateCargoScheduledCarrier.mutateAsync({
          id: latestOnGoingContractOfferCargo.data.scheduledCarrier.id,
          data: {
            vehicleNumber: data.vehicleNumber || null,
            contactName: data.contactName || null,
            contactNameKana: data.contactNameKana || null,
            name: data.name || null,
            phoneNumber: data.phoneNumber || null,
          },
        });
        if (isSuccessStatusCode(result.status)) {
          await Promise.all([
            refetchOfferCargoProcess(),
            getDraftOfferCargo.refetch(),
          ]);
          setGlobalNotification({
            variant: "success",
            message: "配送予定車両を更新しました",
          });
        } else {
          setGlobalNotification({
            variant: "error",
            message: "配送予定車両の更新に失敗しました",
          });
        }
      },
      [
        latestOnGoingContractOfferCargo,
        updateCargoScheduledCarrier,
        refetchOfferCargoProcess,
        getDraftOfferCargo,
        setGlobalNotification,
      ],
    );

  const onDeleteScheduledCarrier: CarrierCargoDetailPageProps["onDeleteScheduledCarrier"] =
    useCallback(async () => {
      if (
        latestOnGoingContractOfferCargo.status !== "success" ||
        !latestOnGoingContractOfferCargo.data?.scheduledCarrier
      ) {
        console.error("scheduledCarrier is not loaded");
        return;
      }
      const result = await deleteCargoScheduledCarrier.mutateAsync({
        id: latestOnGoingContractOfferCargo.data.scheduledCarrier.id,
      });
      if (isSuccessStatusCode(result.status)) {
        await Promise.all([
          refetchOfferCargoProcess(),
          getDraftOfferCargo.refetch(),
        ]);
        setGlobalNotification({
          variant: "success",
          message: "配送予定車両を削除しました",
        });
      } else {
        setGlobalNotification({
          variant: "error",
          message: "配送予定車両の削除に失敗しました",
        });
      }
    }, [
      latestOnGoingContractOfferCargo,
      deleteCargoScheduledCarrier,
      refetchOfferCargoProcess,
      getDraftOfferCargo,
      setGlobalNotification,
    ]);

  return {
    offerCargoProcess,
    myLatestValidDeliveryProposalOfferCargo,
    latestOnGoingContractOfferCargo,
    vehicles,
    onProposeClick,
    onApproveContractClick,
    onRejectContractClick,
    onProposeAgainClick,
    onCreateScheduledCarrier,
    onUpdateScheduledCarrier,
    onDeleteScheduledCarrier,
  };
};
